import type { Customer } from '@gmn-portal/backend';
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Eye, Helicopter, MoreHorizontal, Plus, Users } from 'lucide-react';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Where } from '@/core/api';
import { useCustomers } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import { PageContainer, PageTitle, SimpleBreadcrumbItem } from '@/shared/components/layout';
import {
  SortableHeader,
  TableActiveFilterTags,
  TableCard,
  TableCardContent,
  TableCardFooter,
  TableCardHeader,
  TableLoadingCell,
  TablePagination,
  TableSearchBar,
} from '@/shared/components/tables';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  EmptyTableRow,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderRow,
  TableRow,
} from '@/shared/components/ui';
import { useDebounce } from '@/shared/hooks/use-debounce';
import { useTablePreferences } from '@/shared/services/table-preferences';
import { isGmnAdminGroup } from '@/shared/utils/permissions';
import { getSortParam } from '@/shared/utils/table-utils';

type CustomerOverviewVariant = 'customer' | 'heli-provider';

const ActionCell = ({
  customer,
  onViewDetail,
  basePath,
}: {
  customer: Customer;
  onViewDetail: (id: string) => void;
  basePath: string;
}) => {
  const navigate = useNavigate();
  const { t } = useIntl();

  return (
    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
      <button
        type="button"
        onClick={() => onViewDetail(customer.id)}
        className="p-1.5 hover:bg-secondary text-primary rounded-md transition-colors"
        title={t('common.view')}
      >
        <Eye size={16} />
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="p-1.5 hover:bg-muted text-muted-foreground rounded-md transition-colors"
          >
            <MoreHorizontal size={16} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
          <DropdownMenuItem onClick={() => navigate(`${basePath}/${customer.id}`)}>
            {t('common.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem disabled title={t('customers.deleteNotImplemented')}>
            {t('common.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

type CustomersOverviewProps = {
  variant?: CustomerOverviewVariant;
};

export default function CustomersOverview({ variant = 'customer' }: CustomersOverviewProps) {
  const { t } = useIntl();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isGmnAdmin = isGmnAdminGroup(currentUser);
  const isHeliProviderView = variant === 'heli-provider';
  const basePath = isHeliProviderView
    ? '/gmn/customer-management/helicopter-service-providers'
    : '/gmn/customer-management/customers';
  const pageTitle = isHeliProviderView
    ? t('customers.manageHelicopterServiceProviders')
    : t('customers.manageCustomers');
  const breadcrumbRootLabel = isHeliProviderView
    ? t('customers.helicopterServiceProviders')
    : t('customers.customers');
  const breadcrumbCurrentLabel = isHeliProviderView
    ? t('navigation.items.allHelicopterServiceProviders')
    : t('navigation.items.allCustomers');
  const addLabel = isHeliProviderView
    ? t('customers.addHelicopterServiceProvider')
    : t('customers.addCustomer');
  const searchPlaceholder = isHeliProviderView
    ? t('customers.searchHelicopterServiceProviders')
    : t('customers.searchPlaceholder');

  const tablePreferences = useTablePreferences({
    tableId: 'customers-overview',
    defaultPreferences: {
      pagination: {
        pageIndex: 0,
        pageSize: 10,
      },
      sorting: [],
      columnFilters: [],
      columnVisibility: {},
      searchTerm: '',
    },
  });

  // Load initial preferences
  const preferences = tablePreferences.loadPreferences();

  const [searchTerm, setSearchTerm] = useState(preferences.searchTerm || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [sorting, setSorting] = useState<SortingState>(preferences.sorting || []);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    preferences.columnFilters || []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    preferences.columnVisibility || {}
  );
  const [rowSelection, setRowSelection] = useState({});
  const [pagination, setPagination] = useState(
    preferences.pagination || {
      pageIndex: 0,
      pageSize: 10,
    }
  );

  // Save preferences when they change
  useEffect(() => {
    tablePreferences.savePreferences({
      pagination,
      sorting,
      columnFilters,
      columnVisibility,
      searchTerm,
    });
  }, [pagination, sorting, columnFilters, columnVisibility, searchTerm]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const activeFilterTags = searchTerm
    ? [
        {
          id: 'search',
          label: (
            <>
              <span className="font-medium text-foreground">{t('common.searchLabel')}:</span>
              <span className="truncate max-w-[240px]" title={searchTerm}>
                {searchTerm}
              </span>
            </>
          ),
          onRemove: () => handleSearchChange(''),
        },
      ]
    : [];

  const handleViewDetail = useCallback(
    (id: string) => {
      navigate(`${basePath}/${id}`);
    },
    [navigate, basePath]
  );

  const columns: ColumnDef<Customer>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <SortableHeader column={column} title={t('common.name')} />,
      cell: ({ row }) => {
        const name = row.getValue('name') as string;
        return (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-secondary text-primary flex items-center justify-center font-bold text-xs">
              {name.substring(0, 2).toUpperCase()}
            </div>
            <span className="font-medium text-foreground">{name}</span>
          </div>
        );
      },
      size: 250,
      minSize: 150,
      maxSize: 400,
    },
    {
      accessorKey: 'companyType',
      header: ({ column }) => <SortableHeader column={column} title={t('customers.companyType')} />,
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.getValue('companyType') || '-'}</span>
      ),
      size: 200,
      minSize: 100,
      maxSize: 300,
    },
    {
      accessorKey: 'industry',
      header: ({ column }) => <SortableHeader column={column} title={t('customers.industry')} />,
      cell: ({ row }) => {
        const industry = row.getValue('industry') as string;
        return industry ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground border border-border">
            {industry}
          </span>
        ) : (
          <span className="text-muted-foreground">-</span>
        );
      },
      size: 200,
      minSize: 100,
      maxSize: 300,
    },
    {
      accessorKey: 'website',
      header: ({ column }) => <SortableHeader column={column} title={t('customers.website')} />,
      cell: ({ row }) => {
        const website = row.getValue('website') as string;
        if (!website) return <span className="text-muted-foreground">-</span>;
        const displayUrl = website
          .replace('https://www.', '')
          .replace('https://', '')
          .replace('http://www.', '')
          .replace('http://', '')
          .split('/')[0];
        return (
          <a
            href={website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {displayUrl}
          </a>
        );
      },
      size: 200,
      minSize: 100,
      maxSize: 400,
    },
    {
      accessorKey: 'users',
      header: ({ column }) => <SortableHeader column={column} title={t('customers.userCount')} />,
      cell: ({ row }) => {
        const customer = row.original;
        const userCount = customer.users?.totalDocs ?? 0;
        const maxUsers = customer.userQuota ?? '-';
        return (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Users size={14} />
            <span>
              {userCount} / {maxUsers}
            </span>
          </div>
        );
      },
      size: 150,
      minSize: 100,
      maxSize: 200,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => <SortableHeader column={column} title={t('common.createdAt')} />,
      cell: ({ row }) => {
        const date = row.getValue('createdAt') as string;
        return (
          <span className="text-muted-foreground">
            {date ? DateTime.fromISO(date).toFormat('dd.MM.yyyy') : '-'}
          </span>
        );
      },
      size: 150,
      minSize: 100,
      maxSize: 200,
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">{t('common.actions')}</span>,
      cell: ({ row }) => (
        <ActionCell customer={row.original} onViewDetail={handleViewDetail} basePath={basePath} />
      ),
      size: 80,
      minSize: 60,
      maxSize: 100,
    },
  ];

  const baseWhere: Where = isHeliProviderView
    ? { customerType: { equals: 'heli-provider' } }
    : { or: [{ customerType: { equals: 'customer' } }, { customerType: { exists: false } }] };
  const searchWhere = debouncedSearchTerm ? { name: { like: debouncedSearchTerm } } : null;
  const where = searchWhere ? { and: [baseWhere, searchWhere] } : baseWhere;

  const { data: customersData, isLoading } = useCustomers({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    where,
    sort: getSortParam<Customer>(sorting),
  });

  const customers = customersData?.docs || [];
  const totalPages = customersData?.totalPages || 0;
  const totalDocs = customersData?.totalDocs || 0;

  const table = useReactTable({
    data: customers,
    columns,
    pageCount: totalPages,
    rowCount: totalDocs,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    columnResizeMode: 'onChange',
  });

  return (
    <PageContainer>
      <PageTitle
        icon={isHeliProviderView ? Helicopter : Users}
        breadcrumb={
          <>
            <SimpleBreadcrumbItem>{breadcrumbRootLabel}</SimpleBreadcrumbItem>
            <SimpleBreadcrumbItem isLast>{breadcrumbCurrentLabel}</SimpleBreadcrumbItem>
          </>
        }
        title={pageTitle}
        primaryAction={{
          label: addLabel,
          onClick: () => navigate(`${basePath}/new`),
          icon: <Plus size={16} />,
          disabled: !isGmnAdmin,
        }}
      />

      <TableCard className="mb-4">
        <TableCardHeader>
          <TableSearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            placeholder={searchPlaceholder}
          />
        </TableCardHeader>
      </TableCard>

      {activeFilterTags.length > 0 ? (
        <div className="mb-4">
          <TableActiveFilterTags tags={activeFilterTags} />
        </div>
      ) : null}

      <TableCard>
        <TableCardContent>
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableHeaderRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <TableHead
                      key={header.id}
                      style={{ width: `${header.getSize()}px` }}
                      className={
                        index === 0
                          ? 'pl-6'
                          : index === headerGroup.headers.length - 1
                            ? 'pr-6 text-right'
                            : ''
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                </TableHeaderRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className="cursor-pointer"
                    onClick={() => handleViewDetail(row.original.id)}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}px` }}
                        className={
                          index === 0
                            ? 'pl-6'
                            : index === row.getVisibleCells().length - 1
                              ? 'pr-6'
                              : ''
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : !isLoading ? (
                <EmptyTableRow colSpan={columns.length} />
              ) : (
                <TableLoadingCell colSpan={columns.length} />
              )}
            </TableBody>
          </Table>
        </TableCardContent>

        <TableCardFooter>
          <TablePagination table={table} />
        </TableCardFooter>
      </TableCard>
    </PageContainer>
  );
}
